import { EventEmitter, Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { IEupRxForm, PagedQueryParameters, PagedQueryResult } from '../generalInterfaces';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { DownloadFileService } from '../downloadFile.service';
import { TranslateService } from '@ngx-translate/core';
import { ModalIcon, ModalKeys, NotificationService } from '../notification/notification.service';
import { IBaseOrderDataDto } from '@interfaces/IBaseOrderDataDto';
import { IOrdersStatusesDto } from '@interfaces/IOrdersStatusesDto';
import { TimberService } from '@logging/timber.service';
import { Order } from '@shared/orders/order';
import { RxVersion } from '../../rx/models/rx-version.enum';

@Injectable()
export class OrdersService {
	public orderObservable$: Observable<Order>;
	public OrderUpdated = new EventEmitter<any>();

	constructor(
		private http: EupHttpHandler,
		private eupRoutesService: EupRoutesService,
		private downloadFileService: DownloadFileService,
		private translateService: TranslateService,
		private notificationService: NotificationService,
		private timberService: TimberService
	) {}

	getDeliveredFrontlineOrders(query: PagedQueryParameters): Observable<PagedQueryResult> {
		return this.getOrders(this.eupRoutesService.orders.deliveredFrontlineOrdersUrl, query);
	}

	deleteRx(rxId: string): Observable<any> {
		return this.http.delete(`${this.eupRoutesService.orders.deleteRxUrl}/${rxId}`, { responseType: 'text' });
	}

	getOrders(url: string, query: PagedQueryParameters): Observable<PagedQueryResult> {
		return this.http.get(url, { params: query.createSearchParams() }, true, true);
	}

	getOrderStatuses(selectedCompanyId: number, selectedDoctorId: number): Observable<IOrdersStatusesDto> {
		const params = new HttpParams().set('companyId', selectedCompanyId.toString()).set('doctorId', selectedDoctorId.toString());

		return this.http.get(this.eupRoutesService.orders.getOrderStatusesUrl, { params }, true, false).pipe(
			catchError(err => {
				this.timberService.error(err, { module: 'order.service' });

				return of({
					failedOrderMessage: '',
					isFailedOrderExists: false,
					isPendingOrders: false,
				} as IOrdersStatusesDto);
			})
		);
	}

	getDoctorOrder(params: { orderId: number; doctorId: number; companyId: number }): Observable<Order> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.orderId}`)
			.set('doctorId', `${params.doctorId}`)
			.set('companyId', `${params.companyId}`);

		return this.http.get(this.eupRoutesService.orders.getOrderByIdUrl, { params: queryParams });
	}

	getBaseOrderData(params: { orderId: number }): Observable<IBaseOrderDataDto> {
		const queryParams = new HttpParams().set('orderId', `${params.orderId}`);

		return this.http.get(this.eupRoutesService.orders.getBaseOrderDataUrl, { params: queryParams });
	}

	getOrderCode(orderId: number): Observable<string> {
		const queryParams = new HttpParams().set('orderId', `${orderId}`);

		return this.http.get(this.eupRoutesService.orders.getOrderCode, { params: queryParams });
	}

	validateOrder(params: { id: number }): Observable<boolean> {
		const queryParams = new HttpParams().set('orderId', `${params.id}`);

		return this.http.get(this.eupRoutesService.orders.validateOrderUrl, { params: queryParams });
	}

	getLabOrder(params: { orderId: number | string; doctorId: number | string; companyId: number | string }): Observable<Order> {
		const queryParams = new HttpParams()
			.set('orderId', `${params.orderId}`)
			.set('doctorId', `${params.doctorId}`)
			.set('companyId', `${params.companyId}`);

		return this.http.get(this.eupRoutesService.lab.getOrderByIdUrl, { params: queryParams });
	}

	initLabOrderObservable(params: { orderId: number; doctorId: number; companyId: number }): void {
		this.orderObservable$ = this.getLabOrder(params);
	}

	getProcedureOrCaseTypeDescription(orderOrRxForm: Order | IEupRxForm, procedureEnabled: boolean): string {
		const version = orderOrRxForm.hasOwnProperty('rxVersion')
			? (orderOrRxForm as Order).rxVersion
			: (orderOrRxForm as IEupRxForm).version;

		if (procedureEnabled && (version as RxVersion) === RxVersion.ProcedureFlow) {
			return orderOrRxForm.procedureDescription || '';
		}

		return orderOrRxForm.caseTypeDescription;
	}
	getFileStorage(orderId: number): Observable<number> {
		const params = new HttpParams().set('orderId', orderId.toString());

		return this.http
			.get(this.eupRoutesService.orders.getFileStorage, { params }, true, true)
			.pipe(catchError(err => this.handleError(err)));
	}

	updateLabOrderById(orderId: string, doctorId: string, companyId: string, action: (orderToEdit: Order) => void): void {
		const params = new HttpParams().set('orderId', orderId).set('doctorId', doctorId).set('companyId', companyId);

		this.http.get(this.eupRoutesService.lab.getOrderByIdUrl, { params }).subscribe((order: Order) => {
			if (this.OrderUpdated) {
				if (action) {
					action(order);
				}
				this.OrderUpdated.emit(order);
			}
		});
	}

	startProtocol(link: string, createNewiFrame: boolean): void {
		let ifrm: HTMLIFrameElement = null;

		if (!createNewiFrame) {
			ifrm = document.getElementsByTagName('iframe')[0];
		}
		if (ifrm === null || ifrm === undefined) {
			ifrm = document.createElement('iframe');
		}
		ifrm.setAttribute('src', link);
		ifrm.style.display = 'none';
		document.body.appendChild(ifrm);
	}

	private handleError(err) {
		this.notificationService.show(
			this.translateService.instant('Errors.Error_download_file_title'),
			this.translateService.instant('Errors.Error_download_file_body'),
			{ buttonCombination: ModalKeys.Ok, icon: ModalIcon.Error }
		);

		return throwError(err);
	}
}
